import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, Building2, Users } from "lucide-react";

const Box = ({ title, titleCls = "text-ism-teal", children }) => (
  <div className="space-y-3 mt-4 p-4 bg-white/5 rounded-lg border border-gray-800">
    <h4 className={`text-sm font-medium ${titleCls}`}>{title}</h4>
    {children}
  </div>
);

const Chips = ({ label, items }) => items ? (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <div className="flex flex-wrap gap-1 mt-1">{items.map((v) => <Badge key={v} variant="outline" className="text-xs">{v}</Badge>)}</div>
  </div>
) : null;

const IcpData = ({ data }) => (
  <Box title="Extracted ICP">
    <Chips label="Company Types" items={data.targetCompanyTypes} />
    <Chips label="Industries" items={data.targetIndustries} />
    {data.companySizeRange?.description && <div><p className="text-xs text-gray-500">Company Size</p><p className="text-sm text-white">{data.companySizeRange.description}</p></div>}
    {data.icpSummary && <div><p className="text-xs text-gray-500">Summary</p><p className="text-sm text-gray-300">{data.icpSummary}</p></div>}
  </Box>
);

const EntityList = ({ title, titleCls, items, icon: Icon, primary, secondary, keyOf = primary }) => (
  <Box title={`${title} (${items.length})`} titleCls={titleCls}>
    <div className="max-h-40 overflow-y-auto space-y-2">
      {items.slice(0, 10).map((it, i) => (
        <div key={keyOf(it) || i} className="flex items-center gap-2 text-sm">
          <Icon className="h-3 w-3 text-gray-500" />
          <span className="text-white">{primary(it)}</span>
          {secondary(it) && <span className="text-xs text-gray-500">- {secondary(it)}</span>}
        </div>
      ))}
      {items.length > 10 && <p className="text-xs text-gray-500">...and {items.length - 10} more</p>}
    </div>
  </Box>
);

export const ExtractedData = ({ result }) => {
  const data = result?.extractedData;
  if (!data) return null;
  if (data.targetCompanyTypes || data.icpSummary) return <IcpData data={data} />;
  const accounts = data.accounts || result.accounts;
  if (accounts) return <EntityList title="Extracted Accounts" titleCls="text-green-400" items={accounts} icon={Building2} primary={(a) => a.name || a.companyName} secondary={(a) => a.context} />;
  if (data.contacts) return <EntityList title="Extracted Contacts" titleCls="text-orange-400" items={data.contacts} icon={Users} primary={(c) => c.name} secondary={(c) => c.title} keyOf={(c) => c.email || c.name} />;
  return (
    <div className="mt-4 p-4 bg-white/5 rounded-lg border border-gray-800">
      <h4 className="text-sm font-medium text-ism-teal mb-2">Extracted Data</h4>
      <pre className="text-xs text-gray-400 overflow-auto max-h-40">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
};

export const ParseResult = ({ result }) => {
  if (!result) return null;
  const ok = result.success;
  const records = result.recordsCreated || result.importedRows;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="smart-upload-result">
      <div className={`p-4 rounded-lg border ${ok ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`}>
        <div className="flex items-center gap-2 mb-2">
          {ok ? <CheckCircle2 className="h-5 w-5 text-green-400" /> : <AlertCircle className="h-5 w-5 text-red-400" />}
          <span className={ok ? "text-green-400" : "text-red-400"}>{ok ? "Parsing Complete" : "Parsing Failed"}</span>
        </div>
        {ok && (
          <div className="space-y-1 text-sm text-gray-400">
            {result.documentType && <p>Detected type: <span className="text-white">{result.documentType}</span></p>}
            {result.dataType && <p>Data type: <span className="text-white">{result.dataType}</span></p>}
            {records > 0 && <p>Records created: <span className="text-ism-teal">{records}</span></p>}
            {result.instanceUpdated && <p className="text-ism-teal">✓ Workspace profile updated</p>}
          </div>
        )}
        {!ok && result.error && <p className="text-sm text-red-400">{result.error}</p>}
      </div>
      <ExtractedData result={result} />
    </motion.div>
  );
};
